import {
  apiChangePassword,
  apiGetPasswordPolicy,
  apiLoginByUsername,
  apiForgotPassword,
  apiValidateNewPasswordToken,
  apiChangePasswordWithToken
} from '@/api/authentication';
import { getToken, removeToken, setToken, getUserType, removeUserType, setUserType } from '@/utils/auth';
import jwt_decode from 'jwt-decode';

const token = getToken();
const userType = getUserType();
let userId = '';
let linkedCompanyIDs = [];

if (token) {
  const decodedJwtToken = jwt_decode(token);
  userId = parseInt(decodedJwtToken.sub);
  if (decodedJwtToken.companyIDs) {
    linkedCompanyIDs = decodedJwtToken.companyIDs.split(',').map(c => parseInt(c));
  }
}
export default {
  actions: {
    changePasswordOfLoggedInUser({ commit, state }, passwordChangeInfo) {
      return new Promise((resolve, reject) => {
        apiChangePassword(state.userId, passwordChangeInfo.oldPassword, passwordChangeInfo.newPassword)
          .then(() => {
            commit('SET_PASSWORD_CHANGED', true);
            resolve();
          })
          .catch(error => {
            commit('SET_PASSWORD_CHANGED', false);
            reject(error);
          });
      });
    },

    frontEndLogout({ commit }) {
      return new Promise(resolve => {
        commit('SET_TOKEN', '');
        commit('SET_ROLES', []);
        commit('SET_DOMAINS', []);
        removeToken();
        removeUserType();
        resolve();
      });
    },

    getPasswordPolicy({ commit, state }) {
      return new Promise((resolve, reject) => {
        apiGetPasswordPolicy()
          .then(response => {
            //
            // Since mockjs does not support custom status codes, it can only be hacked like this
            if (!response.data) {
              reject(new Error('Failed to load password policy.'));
            }
            const data = {
              ...response.data,
              isFromDatabase: true
            };
            commit('SET_PASSWORD_POLICY', data);
            resolve(response);
          })
          .catch(error => {
            reject(error);
          });
      });
    },

    getUserInfo({ commit, state, dispatch }, params) {
      return new Promise((resolve, reject) => {
        if (!params) {
          params = [];
        }

        const standardUserYN = state.userType;

        if (standardUserYN === true || standardUserYN === 'true') {
          const withPermissions = params.withPermissions || true;
          const withDomains = params.withDomains || true;

          dispatch(
            'standardUser/fetchUser',
            { id: state.userId, withPermissions: withPermissions, withDomains: withDomains },
            { root: true }
          )
            .then(response => {
              if (!response.data) {
                reject(new Error('Verification failed, please login again.'));
              }
              const data = response.data;
              if (withPermissions && data.roles && data.roles.length > 0) {
                // Verify that the returned roles are a non-null array
                commit('SET_ROLES', data.roles);
              } else {
                reject(new Error('getInfo: roles must be a non-null array!'));
              }
              if (withDomains && data.domains) {
                const domains = [];
                data.domains.forEach(domainDTO => {
                  const domain = {
                    id: domainDTO.domainID,
                    ...domainDTO
                  };
                  delete domain.domainID;
                  domains.push(domain);
                });
                commit('SET_DOMAINS', domains);
              }
              if (data.workshops) {
                const workshops = [];
                data.workshops.forEach(workshopDTO => {
                  const workshop = {
                    id: workshopDTO.workshopID,
                    name: workshopDTO.name
                  };
                  workshops.push(workshop);
                });
                commit('SET_WORKSHOPS', workshops);
              }
              commit('SET_USER_PERMISSIONS', data.permissions);
              commit('SET_USER_BLOCKED_NOTIFICATIONS', data.blockedNotificationCodes);
              commit('SET_NAME', data.name);
              commit('SET_CODE', data.code);
              commit('SET_EMAIL', data.email);
              commit('SET_DESCRIPTION', data.description);
              commit('SET_AVATAR', data.avatar);
              resolve(response);
            })
            .catch(error => {
              reject(error);
            });
        } else {
          dispatch('storeUser/fetchStoreUser', state.userId, { root: true })
            .then(response => {
              if (!response.data) {
                reject(new Error('Verification failed, please login again.'));
              }
              const data = response.data;
              commit('SET_USER_PERMISSIONS', data.permissions);
              commit('SET_NAME', data.name);
              commit('SET_CODE', data.code);
              commit('SET_EMAIL', data.email);
              commit('SET_DESCRIPTION', data.description);
              commit('SET_AVATAR', data.avatar);
              resolve(response);
            })
            .catch(error => {
              reject(error);
            });
        }
      });
    },

    forgotPassword({ commit }, userInfo) {
      return apiForgotPassword(userInfo.username, userInfo.email);
    },

    validateTokenForNewPassword({ commit }, token) {
      return apiValidateNewPasswordToken(token);
    },

    changePasswordWithToken({ commit }, tokenInfo) {
      return apiChangePasswordWithToken(tokenInfo.password, tokenInfo.token);
    },

    logOut({ commit, state }) {
      return new Promise((resolve, reject) => {
        try {
          commit('SET_TOKEN', '');
          commit('SET_ROLES', []);
          commit('SET_DOMAINS', []);
          removeToken();
          removeUserType();
          resolve();
        } catch (err) {
          reject(err);
        }
      });
    },

    loginByUsername({ commit }, userInfo) {
      return new Promise((resolve, reject) =>
        apiLoginByUsername(userInfo.code, userInfo.password)
          .then(response => {
            const payload = response.data.token.split('.')[1];
            const jwtPayload = JSON.parse(atob(payload));
            const userId = parseInt(jwtPayload.sub);
            if (response.data.standardUserYN) {
              const linkedCompanyIDs = jwtPayload.companyIDs.split(',').map(c => parseInt(c));
              commit('SET_LINKED_COMPANIES', linkedCompanyIDs);
            }
            commit('SET_TOKEN', response.data.token);
            commit('SET_USER_TYPE', response.data.standardUserYN);
            commit('SET_USER_ID', userId);
            setToken(response.data.token);
            setUserType(response.data.standardUserYN);
            resolve();
          })
          .catch(error => {
            reject(error);
          })
      );
    },

    persistUser({ dispatch }, userInfo) {
      return new Promise((resolve, reject) => {
        dispatch('standardUser/updateSelf', userInfo, { root: true })
          .then(() => {})
          .catch(error => {
            reject(error);
          });
      });
    },

    updateLoggedInUser({ commit, state, dispatch }, userInfo) {
      return new Promise((resolve, reject) => {
        const user = {
          ...userInfo,
          id: state.userId
        };
        dispatch('standardUser/updateSelf', user, { root: true })
          .then(() => {
            commit('SET_NAME', userInfo.name);
            commit('SET_EMAIL', userInfo.email);
            commit('SET_DESCRIPTION', userInfo.description);
            commit('SET_AVATAR', userInfo.avatar);
            commit('SET_USER_BLOCKED_NOTIFICATIONS', userInfo.blockedNotificationCodes);
            resolve();
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    insertBlockedNotification({ commit }, notificationCode) {
      commit('INSERT_USER_BLOCKED_NOTIFICATION', notificationCode);
    }
  },
  mutations: {
    SET_AVATAR: (state, avatar) => {
      state.avatar = avatar;
    },
    SET_CODE: (state, code) => {
      state.code = code;
    },
    SET_DOMAINS: (state, domains) => {
      state.domains = domains;
    },
    SET_DESCRIPTION: (state, description) => {
      state.description = description;
    },
    SET_EMAIL: (state, email) => {
      state.email = email;
    },
    SET_NAME: (state, name) => {
      state.name = name;
    },
    SET_PASSWORD_CHANGED: (state, passwordChanged) => {
      state.passwordChanged = passwordChanged;
    },
    SET_TOKEN: (state, token) => {
      state.token = token;
    },
    SET_USER_TYPE: (state, userType) => {
      state.userType = userType;
    },
    SET_PASSWORD_POLICY: (state, passwordPolicy) => {
      state.passwordPolicy = passwordPolicy;
    },
    SET_ROLES: (state, roles) => {
      state.roles = roles;
    },
    SET_SETTING: (state, setting) => {
      state.setting = setting;
    },
    SET_STATUS: (state, status) => {
      state.status = status;
    },
    SET_USER_BLOCKED_NOTIFICATIONS: (state, blockedNotificationCodes) => {
      state.blockedNotificationCodes = blockedNotificationCodes || [];
    },
    INSERT_USER_BLOCKED_NOTIFICATION: (state, notificationCode) => {
      state.blockedNotificationCodes.push(notificationCode);
    },
    SET_USER_ID: (state, id) => {
      state.userId = id;
    },
    SET_USER_PERMISSIONS: (state, permissions) => {
      state.permissions = permissions;
    },
    SET_WORKSHOPS: (state, workshops) => {
      state.workshops = workshops;
    },
    SET_LINKED_COMPANIES: (state, linkedCompanyIDs) => {
      state.linkedCompanyIDs = linkedCompanyIDs;
    }
  },

  state: {
    avatar: '',
    code: '',
    description: '',
    email: '',
    name: '',
    passwordChanged: false,
    passwordPolicy: {
      minimumLength: 0,
      requireDigit: false,
      requireLowercase: false,
      isFromDatabase: false,
      requireUppercase: false,
      requireNonAlphanumeric: false
    },
    blockedNotificationCodes: [],
    permissions: [],
    domains: [],
    roles: [],
    setting: {
      articlePlatform: []
    },
    status: '',
    user: '',
    userId,
    token,
    linkedCompanyIDs,
    userType,
    workshops: []
  }
};
